import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";

/* Password Validator */
function validatePassword(pw) {
  const hasMinLength = pw.length >= 8;
  const hasNumber = /\d/.test(pw);
  const hasUppercase = /[A-Z]/.test(pw);
  const hasLowercase = /[a-z]/.test(pw);
  const hasSymbol = /[^A-Za-z0-9]/.test(pw);
  
  return hasMinLength && hasNumber && hasUppercase && hasLowercase && hasSymbol;
}

function Signup() {
  const [step, setStep] = useState("signup");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  
  // NEW: State for error handling and loading
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors

    if (!validatePassword(password)) {
      setError("Password does not meet requirements.");
      return;
    }

    if (password !== retypePassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, companyName }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // This will catch the 409 Conflict (UsernameExistsException) 
        // and display your custom message from the backend.
        throw new Error(data.error || "Signup failed");
      }

      setTempPassword(password);
      setStep("verify");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    setResending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/resendConfirmationCode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Failed to resend code");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const confirmationCode = e.target.confirmationCode.value;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/confirmSignup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, confirmationCode, password: tempPassword }),
        credentials: "include"
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Verification failed");
      }
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>{step === "signup" ? "Sign Up" : "Verify Email"}</h2>

        {/* --- Error Message Display --- */}
        {error && <div className="error-banner">{error}</div>}

        {step === "signup" && (
          <form className="login-form" onSubmit={handleSignup}>
            <input
              type="text"
              name="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={error && error.includes("email") ? "input-error" : ""}
              required
            />

            <input
              type="text"
              name="companyName"
              placeholder="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />

            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordTouched(true);
              }}
              className={
                !passwordTouched ? "" : 
                validatePassword(password) ? "input-success" : "input-error"
              }
            />
            
            <div className="password-hints-inline">
              <span className={password.length >= 8 ? "done" : ""}>8+ chars</span>
              <span className={/[A-Z]/.test(password) ? "done" : ""}>Uppercase</span>
              <span className={/[a-z]/.test(password) ? "done" : ""}>Lowercase</span>
              <span className={/\d/.test(password) ? "done" : ""}>Number</span>
              <span className={/[^A-Za-z0-9]/.test(password) ? "done" : ""}>Symbol</span>
            </div>

            <input
              type={showPassword ? "text" : "password"}
              name="retypePassword"
              placeholder="Retype Password"
              required
              value={retypePassword}
              onChange={(e) => {
                setRetypePassword(e.target.value);
                setPasswordTouched(true);
              }}
              className={
                !passwordTouched || !retypePassword ? "" : 
                (password === retypePassword) ? "input-success" : "input-error"
              }
            />

            <div className="password-row">
              <span
                className="show-password-link"
                onClick={() => setShowPassword(p => !p)}
              >
                {showPassword ? "Hide password" : "Show password"}
              </span>
            </div>

            <button type="submit" disabled={loading}>
                {loading ? "Creating Account..." : "Sign Up"}
            </button>
          </form>
        )}

        {step === "verify" && (
          <div className="login-form">
            <p style={{fontSize: '14px', marginBottom: '15px', textAlign: 'center'}}>
                We sent a code to <strong>{email}</strong>
            </p>
            <form onSubmit={handleVerify} style={{width: '100%'}}>
              <input
                type="text"
                name="confirmationCode"
                placeholder="Verification Code"
                className={error ? "input-error" : ""}
                required
              />
              <button type="submit" disabled={loading}>
                {loading ? "Verifying..." : "Verify Code"}
              </button>
            </form>

            <button
                className="show-password-link"
                style={{background: 'none', color: '#4f46e5', boxShadow: 'none', marginTop: '10px'}}
                disabled={resending}
                onClick={handleResendCode}
            >
              {resending ? "Sending..." : "Resend Code"}
            </button>
          </div>
        )}

        <p>Already have an account? <Link to="/login">Login</Link></p>
      </div>
    </div>
  );
}

export default Signup;